// Pause Menu: a top-right "MENU" button that pauses the game and opens an overlay with
// How to Play, Abilities, a Bestiary, and a (placeholder) Leaderboard. Built from the
// balance data so it stays in sync. Pausing/resuming is delegated to the caller via the
// onToggle callback.

import { ALTAR, ENEMY_SHOT, WET } from '../game/balance';
import { abilityDefinitionsForLevel } from '../game/activeAbilities';
import { VARIANT } from '../game/variant';
import { HIDDEN_ENEMY_NAMES } from '../game/hiddenLabels';
import type { EnemyType } from '../game/balance';
import type { LevelDefinition } from '../game/levels';
import { ABILITY_ICONS, makeIconCanvas } from './icons';
import { spriteToCanvas } from '../render/pixelArt';
import type { LeaderboardClient } from '../net/leaderboardClient';
import { renderLeaderboard as renderBoard } from './leaderboardView';
import { isCompactTouchLayout } from './platform';

const TABS = ['How to Play', 'Abilities', 'Bestiary', 'Leaderboard'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  'How to Play': 'HOW',
  Abilities: 'POWERS',
  Bestiary: 'MOBS',
  Leaderboard: 'SCORES',
};

function makeAccessibleButton(element: HTMLDivElement, label: string, action: () => void): void {
  element.setAttribute('role', 'button');
  element.setAttribute('aria-label', label);
  element.tabIndex = 0;
  element.addEventListener('click', action);
  element.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    action();
  });
}

const COMPACT_MENU_HELP_FONT_PX = 12;
const COMPACT_MENU_CONTENT_FONT_PX = 12;
const COMPACT_MENU_META_FONT_PX = 11;
const COMPACT_MENU_TITLE_FONT_PX = 14;
const DESKTOP_MENU_FONT_PX = 13;

const BASE_NAMES: Record<string, string> = {
  slime: 'Slime Blob',
  spore: 'Spore Swarmer',
  glass: 'Glass-Golem',
  wraith: 'Ink Wraith',
  vat: 'Bloated Vat',
  scorpion: 'Acid Scorpion',
  mortar: 'Mortar Mold',
  flask: 'Overflowing Flask',
  construct: 'Quicksilver Construct',
  catalyst: 'The Catalyst',
  yeastling: 'Yeastling',
  glassGnat: 'Glass Gnat',
  rootling: 'Rootling',
  kegCrab: 'Keg Crab',
  mycelialSlinger: 'Mycelial Slinger',
  copperWasp: 'Copper Wasp',
  bloomBrute: 'Bloom Brute',
  vatmother: 'The Vatmother',
  copperhorn: 'Copperhorn Stag',
  verdantAlembic: 'The Verdant Alembic',
  coalMite: 'Coal Mite',
  prismDrifter: 'Prism Drifter',
  railSentinel: 'Rail Sentinel',
  kilnHound: 'Kiln Hound',
  starScribe: 'Star Scribe',
  magnetBeetle: 'Magnet Beetle',
  conduitShard: 'Conduit Shard',
  conduitGolem: 'Conduit Golem',
  railwright: 'The Railwright',
  meteorHart: 'The Meteor Hart',
  starfallEngine: 'The Starfall Engine',
  // NinjaKid Dojo - names and spellings straight from the kid's sketchbook.
  evilChair: 'Evil Chair',
  mosquito: 'Mosquito',
  penguin: 'Penguin',
  snake: 'Snake',
  snakelet: 'Snakelet',
  squid: 'Squid',
  evilHoover: 'Evil Hoover',
  cerbere: 'Cerbere',
  giantSpider: 'Giant Spider',
  dragon: 'Dragon',
  centaurus: 'Centaurus',
  ciclopus: 'Ciclopus',
  trole: 'Trole',
  goldEagle: 'Gold Eagle',
  rinocerite: 'Rinocerite',
  dakne: 'Dakne',
  collector: 'The Collector',
  supremeVlad: 'Supreme Vlad',
  odin: 'Odin',
  mosquitaro: 'Mosquitaro',
  ragam: 'Ragam',
  terence: 'Terence',
};

// On /hidden, the kids' enemy renames override the base names (bosses keep theirs).
const NAMES: Record<string, string> =
  VARIANT === 'hidden' ? { ...BASE_NAMES, ...HIDDEN_ENEMY_NAMES } : BASE_NAMES;

const KIND_INFO: Record<string, string> = {
  chase: 'Chases you directly.',
  weave: 'Weaves side-to-side - hard to hit.',
  charge: 'Winds up, then dashes at you.',
  split: 'Splits into smaller fragments when killed.',
  ranged: `Keeps its distance and lobs shots (${ENEMY_SHOT.damage} dmg).`,
  summon: 'Periodically summons more enemies.',
  stationary: 'Rooted in place. Destroy it before it reshapes the arena.',
};

/** Arena-specific enemies earn a readable gameplay explanation instead of exposing an
 * internal spawn key or inheriting an inaccurate generic behavior label. */
const ENEMY_INFO: Record<string, string> = {
  coalMite: 'Slow direct chaser. Its ember sparks mark the path it took.',
  prismDrifter: 'Fragile flier that approaches on irregular arcs.',
  railSentinel: 'Stationary pylon that arms an extra Gravity Rail if ignored.',
  kilnHound: 'Heavy charger that closes distance quickly along an active rail.',
  starScribe: 'Ranged caster whose star fragments ricochet from cover.',
  magnetBeetle: 'Pulls nearby XP and enemies into a cluster before chasing.',
  conduitShard: 'Fast fragment released when a Conduit Golem breaks apart.',
  conduitGolem: 'Elite body that splits into two fast Conduit Shards on defeat.',
  railwright: 'Summons Rail Sentinels and schedules paired rail bursts.',
  meteorHart: 'Charge construct that reroutes active rails toward itself.',
  starfallEngine: 'Final conduit core: star barrages plus two-rail overloads.',
  evilHoover: 'Sucks you toward it while it chases - fight the pull.',
  snake: 'Splits into two speedy Snakelets when killed.',
  snakelet: 'Fast fragment of a defeated Snake.',
  squid: 'Keeps its distance and spits slow ink shots.',
  dragon: 'Breathes fireballs from afar.',
  trole: 'Lobs heavy rocks from a distance.',
  goldEagle: 'Fast and slippery - worth a pile of extra experience.',
  rinocerite: 'Big wrath: winds up, then flattens whatever it hits.',
  dakne: 'First exam: a fast horned charger.',
  collector: 'Summons the Evil Chairs it has collected.',
  supremeVlad: 'Vampire lord that calls swarms of Mosquitos.',
  odin: 'Hurls ricocheting lightning bolts from afar.',
  mosquitaro: 'The Mosquito grown monstrous - fast and erratic.',
  ragam: 'Barrages you with heavy pink bolts.',
  terence: 'The final exam. Fast, furious, and very Terence.',
};

function formatRunDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder === 0 ? `${minutes} minutes` : `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export class Menu {
  readonly button: HTMLDivElement;
  private readonly backdrop: HTMLDivElement;
  private readonly tabRow: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly tabButtons = new Map<Tab, HTMLDivElement>();
  private readonly compact: boolean;
  private exitConfirmation?: HTMLDivElement;
  private exitCancelButton?: HTMLButtonElement;
  private exitTrigger?: HTMLDivElement;
  private open = false;
  private active: Tab = 'How to Play';

  constructor(
    mount: HTMLElement,
    private readonly onToggle: (open: boolean) => void,
    private readonly client: LeaderboardClient,
    private readonly getLevelDefinition: () => LevelDefinition,
    private readonly onExit?: () => void,
  ) {
    const compact = isCompactTouchLayout();
    this.compact = compact;

    // Top-right controls: a leaderboard pop-out icon + the MENU button.
    const controls = document.createElement('div');
    Object.assign(controls.style, {
      position: 'absolute',
      top: '8px',
      right: compact ? 'calc(34px + env(safe-area-inset-right, 0px))' : '12px',
      display: 'flex',
      gap: compact ? '6px' : '8px',
      alignItems: 'center',
      pointerEvents: 'none',
      userSelect: 'none',
    });
    const lb = document.createElement('div');
    lb.textContent = 'LB';
    lb.title = 'Leaderboard';
    Object.assign(lb.style, {
      padding: compact ? '4px 8px' : '4px 9px',
      border: '1px solid #4a4366',
      borderRadius: '5px',
      background: 'rgba(16,12,24,0.85)',
      font: `${compact ? 11 : 15}px "Silkscreen", ui-monospace, monospace`,
      cursor: 'pointer',
      pointerEvents: 'auto',
    });
    makeAccessibleButton(lb, 'Open leaderboard', () => this.openTab('Leaderboard'));

    this.button = document.createElement('div');
    this.button.textContent = 'MENU';
    Object.assign(this.button.style, {
      padding: compact ? '4px 9px' : '5px 12px',
      border: '1px solid #4a4366',
      borderRadius: '5px',
      background: 'rgba(16,12,24,0.85)',
      font: `${compact ? 11 : 13}px "Silkscreen", ui-monospace, monospace`,
      color: '#e8e4f0',
      cursor: 'pointer',
      pointerEvents: 'auto',
      letterSpacing: '1px',
    });
    this.button.setAttribute('aria-expanded', 'false');
    makeAccessibleButton(this.button, 'Open game menu', () => this.toggle());
    controls.append(lb, this.button);
    mount.appendChild(controls);

    // Dim backdrop (click outside the panel closes).
    this.backdrop = document.createElement('div');
    Object.assign(this.backdrop.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      background: 'rgba(8,6,14,0.78)',
      pointerEvents: 'auto',
      zIndex: '20',
    });
    this.backdrop.addEventListener('click', () => this.hide());
    mount.appendChild(this.backdrop);

    // Panel.
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: compact ? 'min(700px, calc(100vw - 34px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))' : 'min(760px, 92vw)',
      maxHeight: compact ? 'calc(100vh - 30px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' : '86vh',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #4a4366',
      borderRadius: compact ? '6px' : '8px',
      background: 'rgba(14,11,22,0.97)',
      font: `${compact ? COMPACT_MENU_CONTENT_FONT_PX : DESKTOP_MENU_FONT_PX}px "Silkscreen", ui-monospace, monospace`,
      color: '#e8e4f0',
      overflow: 'hidden',
    });
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Brewmancer game menu');
    panel.setAttribute('aria-modal', 'true');
    panel.addEventListener('click', (e) => e.stopPropagation());
    this.backdrop.appendChild(panel);

    // Header: title + close.
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: compact ? '8px 11px' : '12px 16px',
      borderBottom: '1px solid #2a2438',
    });
    const title = document.createElement('div');
    title.textContent = compact ? 'MENU' : 'BREWMANCER';
    Object.assign(title.style, { fontSize: compact ? `${COMPACT_MENU_TITLE_FONT_PX}px` : '18px', letterSpacing: compact ? '2px' : '3px', color: '#ffd9a0' });
    const actions = document.createElement('div');
    Object.assign(actions.style, { display: 'flex', alignItems: 'center', gap: compact ? '10px' : '14px' });
    if (this.onExit !== undefined) {
      const exit = document.createElement('div');
      exit.textContent = 'EXIT RUN';
      Object.assign(exit.style, { cursor: 'pointer', color: '#ff8a98', fontSize: compact ? '11px' : '12px', padding: compact ? '2px 4px' : '2px 0' });
      makeAccessibleButton(exit, 'Exit this run', () => this.showExitConfirmation());
      this.exitTrigger = exit;
      actions.appendChild(exit);
    }
    const close = document.createElement('div');
    close.textContent = compact ? 'X' : 'CLOSE';
    Object.assign(close.style, { cursor: 'pointer', color: '#cfc8e0', fontSize: compact ? `${COMPACT_MENU_TITLE_FONT_PX}px` : '12px', padding: compact ? '2px 6px' : '2px 0' });
    makeAccessibleButton(close, 'Close game menu', () => this.hide());
    actions.appendChild(close);
    header.append(title, actions);
    panel.appendChild(header);

    const content = document.createElement('div');
    Object.assign(content.style, {
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      flex: '1 1 auto',
      minHeight: '0',
      overflow: 'hidden',
    });
    panel.appendChild(content);

    if (this.onExit !== undefined) {
      this.exitConfirmation = document.createElement('div');
      Object.assign(this.exitConfirmation.style, {
        position: 'absolute',
        inset: '0',
        zIndex: '30',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? '14px' : '20px',
        background: 'rgba(8, 6, 14, 0.88)',
      });
      this.exitConfirmation.setAttribute('role', 'alertdialog');
      this.exitConfirmation.setAttribute('aria-modal', 'true');
      this.exitConfirmation.setAttribute('aria-hidden', 'true');
      this.exitConfirmation.setAttribute('aria-labelledby', 'exit-run-title');
      this.exitConfirmation.setAttribute('aria-describedby', 'exit-run-description');
      this.exitConfirmation.addEventListener('click', (event) => event.stopPropagation());

      const dialog = document.createElement('div');
      Object.assign(dialog.style, {
        width: compact ? 'min(360px, calc(100% - 12px))' : 'min(400px, calc(100% - 24px))',
        padding: compact ? '18px' : '24px',
        border: '1px solid #5a4f72',
        borderRadius: '6px',
        background: '#100c19',
        boxShadow: '0 10px 32px rgba(0, 0, 0, 0.55)',
        textAlign: 'center',
      });

      const dialogTitle = document.createElement('div');
      dialogTitle.id = 'exit-run-title';
      dialogTitle.textContent = 'EXIT RUN?';
      Object.assign(dialogTitle.style, {
        color: '#ff8a98',
        fontSize: compact ? `${COMPACT_MENU_TITLE_FONT_PX}px` : '18px',
        letterSpacing: '2px',
        marginBottom: compact ? '10px' : '12px',
      });

      const description = document.createElement('div');
      description.id = 'exit-run-description';
      description.textContent = 'CURRENT RUN PROGRESS WILL BE LOST.';
      Object.assign(description.style, {
        color: '#cdc7dd',
        fontSize: compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '13px',
        lineHeight: '1.5',
        marginBottom: compact ? '16px' : '20px',
      });

      const confirmationActions = document.createElement('div');
      Object.assign(confirmationActions.style, {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: compact ? '12px' : '14px',
      });

      const makeConfirmationButton = (label: string, destructive: boolean): HTMLButtonElement => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        Object.assign(button.style, {
          minHeight: compact ? '42px' : '44px',
          border: `2px solid ${destructive ? '#8a3548' : '#8b7130'}`,
          borderRadius: '5px',
          background: destructive ? '#2b121d' : '#3b2d0c',
          color: destructive ? '#ff9aaa' : '#ffd66b',
          font: `${compact ? 12 : 13}px "Silkscreen", ui-monospace, monospace`,
          cursor: 'pointer',
          letterSpacing: '1px',
        });
        return button;
      };

      this.exitCancelButton = makeConfirmationButton('NO', false);
      this.exitCancelButton.addEventListener('click', () => this.hideExitConfirmation());
      const confirmExit = makeConfirmationButton('YES', true);
      confirmExit.addEventListener('click', () => {
        this.hideExitConfirmation(false);
        this.onExit?.();
      });
      confirmationActions.append(this.exitCancelButton, confirmExit);
      dialog.append(dialogTitle, description, confirmationActions);
      this.exitConfirmation.appendChild(dialog);
      panel.appendChild(this.exitConfirmation);
    }

    // Tab row.
    this.tabRow = document.createElement('div');
    Object.assign(this.tabRow.style, {
      display: 'flex',
      flexDirection: compact ? 'column' : 'row',
      gap: compact ? '6px' : '4px',
      padding: compact ? '9px' : '10px 16px 0',
      flex: compact ? '0 0 124px' : '0 0 auto',
      flexWrap: compact ? 'nowrap' : 'wrap',
      borderRight: compact ? '1px solid #2a2438' : 'none',
    });
    for (const t of TABS) {
      const b = document.createElement('div');
      b.textContent = compact ? TAB_LABELS[t] : t;
      Object.assign(b.style, {
        padding: compact ? '9px 10px' : '6px 12px',
        border: '1px solid #2a2438',
        borderBottom: compact ? '1px solid #2a2438' : 'none',
        borderRadius: compact ? '5px' : '5px 5px 0 0',
        cursor: 'pointer',
        fontSize: compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '12px',
        color: '#9a93b0',
        textAlign: compact ? 'left' : 'center',
        letterSpacing: compact ? '1px' : '0',
      });
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', 'false');
      makeAccessibleButton(b, `${t} tab`, () => this.select(t));
      this.tabRow.appendChild(b);
      this.tabButtons.set(t, b);
    }
    content.appendChild(this.tabRow);

    // Scrollable body.
    this.body = document.createElement('div');
    Object.assign(this.body.style, {
      padding: compact ? '10px 12px' : '16px',
      overflowY: 'auto',
      lineHeight: compact ? '1.42' : '1.5',
      fontSize: compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : `${DESKTOP_MENU_FONT_PX}px`,
      borderTop: compact ? 'none' : '1px solid #2a2438',
      flex: '1 1 auto',
      minWidth: '0',
    });
    this.body.setAttribute('role', 'tabpanel');
    this.body.setAttribute('aria-label', 'Game menu content');
    content.appendChild(this.body);
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    this.open = true;
    this.backdrop.style.display = 'block';
    this.button.setAttribute('aria-expanded', 'true');
    this.select(this.active);
    this.onToggle(true);
  }

  hide(): void {
    this.hideExitConfirmation(false);
    if (!this.open) return;
    this.open = false;
    this.backdrop.style.display = 'none';
    this.button.setAttribute('aria-expanded', 'false');
    this.onToggle(false);
  }

  private showExitConfirmation(): void {
    if (this.exitConfirmation === undefined) return;
    this.exitConfirmation.style.display = 'flex';
    this.exitConfirmation.setAttribute('aria-hidden', 'false');
    this.exitCancelButton?.focus();
  }

  private hideExitConfirmation(restoreFocus = true): void {
    if (this.exitConfirmation === undefined) return;
    this.exitConfirmation.style.display = 'none';
    this.exitConfirmation.setAttribute('aria-hidden', 'true');
    if (restoreFocus) this.exitTrigger?.focus();
  }

  /** Open the menu directly on a given tab (used by the leaderboard pop-out button). */
  openTab(tab: Tab): void {
    this.active = tab;
    if (this.open) this.select(tab);
    else this.show();
  }

  private select(tab: Tab): void {
    this.active = tab;
    for (const [t, b] of this.tabButtons) {
      const on = t === tab;
      b.style.background = on ? (this.compact ? '#211936' : '#14111c') : 'transparent';
      b.style.color = on ? '#ffd9a0' : '#9a93b0';
      b.style.borderColor = on ? '#4a4366' : '#2a2438';
      b.setAttribute('aria-selected', String(on));
    }
    this.body.replaceChildren();
    if (tab === 'How to Play') this.renderHowTo();
    else if (tab === 'Abilities') this.renderAbilities();
    else if (tab === 'Bestiary') this.renderBestiary();
    else this.renderLeaderboard();
  }

  private renderHowTo(): void {
    const level = this.getLevelDefinition();
    const runDuration = formatRunDuration(level.runLengthSec);
    const lines: [string, string][] = this.compact
      ? [
          ['Move', 'Drag anywhere on the playfield. Weapons fire automatically at enemies in view.'],
          ['Level up', 'Collect XP. When the draft opens, tap an upgrade card, then confirm your choice.'],
          ['Abilities', 'Every 5 levels unlocks a power. Tap its icon; the cooldown number appears on the icon.'],
          ['Reactions', 'Upgrade weapons high enough to discover fused attacks.'],
          ['Terrain', `Rocks block shots. Water slows you for ~${WET.durationSec}s after you leave it.`],
          ['Altar', `Stand on the glowing altar to heal ${Math.round(ALTAR.healFrac * 100)}% HP, then wait for recharge.`],
          ['Survive', `Outlast ${runDuration}. Opening MENU pauses the run.`],
        ]
      : [
          ['Move', 'Steer with the ARROW KEYS. Your weapons fire automatically at the nearest enemy in view.'],
          ['Level up', 'Kill enemies to drop XP. Fill the bar to level up - the game pauses and you draft 1 of 3 upgrades. Select with arrows, number keys, controller, or touch, then confirm.'],
          ['Abilities', 'Every 5 levels you unlock an active ability. Tap A S D F Z X C V to cast (see the Abilities tab). Each has a cooldown shown on the left bar.'],
          ['Reactions', 'Level a weapon high enough and a Reaction may appear in the draft - it fuses two elements into a stronger combined weapon.'],
          ['Boulders', 'Half-buried rocks are solid: you and enemies slide around them, and they BLOCK projectiles (yours and theirs). Use them as cover.'],
          ['Puddles', `Water pools are passable, but stepping in makes you WET - move speed is halved, and it lingers for ~${WET.durationSec}s after you leave. Kite fast enemies through them to slow the swarm.`],
          ['Healing altar', `A glowing altar sits near a corner. Stand on it to heal ${Math.round(ALTAR.healFrac * 100)}% of max HP, then it dims and recharges over ${ALTAR.cooldownSec}s.`],
          ['Survive', `Bosses arrive on a timer and the horde escalates. Last ${runDuration} to win. ESC / P pauses - or open MENU (top-right) any time.`],
        ];
    if (level.gravityRails !== undefined) {
      const railText = `Pylons warn for ${level.gravityRails.telegraphSec}s with a cyan arrow, then the lane slides you, enemies, XP, and loose shots in that direction.`;
      lines.splice(this.compact ? 5 : 6, 0, ['Gravity Rails', railText]);
    }
    for (const [head, text] of lines) {
      const row = document.createElement('div');
      row.style.marginBottom = this.compact ? '8px' : '12px';
      row.style.fontSize = this.compact ? `${COMPACT_MENU_HELP_FONT_PX}px` : 'inherit';
      const h = document.createElement('span');
      h.textContent = `${head}: `;
      h.style.color = '#ffd9a0';
      const t = document.createElement('span');
      t.textContent = text;
      t.style.color = '#cdc7dd';
      row.append(h, t);
      this.body.appendChild(row);
    }
  }

  private renderAbilities(): void {
    const abilities = abilityDefinitionsForLevel(this.getLevelDefinition());
    const intro = document.createElement('div');
    intro.textContent = this.compact
      ? 'Tap unlocked icons. A number on the icon means it is still cooling down.'
      : 'Unlocked one every 5 levels. Tap the hotkey to cast. Movement is on the arrow keys, so the letter keys are free for abilities.';
    Object.assign(intro.style, { color: '#9a93b0', marginBottom: this.compact ? '12px' : '14px', fontSize: this.compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '12px' });
    this.body.appendChild(intro);

    for (const def of abilities) {
      const hex = `#${def.color.toString(16).padStart(6, '0')}`;
      const row = document.createElement('div');
      Object.assign(row.style, { display: 'flex', gap: this.compact ? '11px' : '12px', alignItems: 'flex-start', marginBottom: this.compact ? '11px' : '12px' });

      const icon = makeIconCanvas(ABILITY_ICONS[def.key], def.color, this.compact ? 3 : 3);
      icon.style.flex = '0 0 auto';
      icon.style.marginTop = '2px';

      const info = document.createElement('div');
      const name = document.createElement('div');
      const abilityName = document.createElement('b');
      abilityName.textContent = def.name;
      abilityName.style.color = hex;
      const meta = document.createElement('span');
      meta.textContent = this.compact
        ? ` Lv ${def.unlockLevel} - ${def.cooldownSec}s`
        : ` key ${def.label} - unlocks Lv ${def.unlockLevel} - ${def.cooldownSec}s cooldown`;
      Object.assign(meta.style, { color: '#8a83a0', fontSize: `${this.compact ? COMPACT_MENU_META_FONT_PX : 11}px` });
      name.append(abilityName, meta);
      const desc = document.createElement('div');
      desc.textContent = def.desc;
      Object.assign(desc.style, { color: '#cdc7dd', fontSize: this.compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '12px' });
      info.append(name, desc);
      row.append(icon, info);
      this.body.appendChild(row);
    }
  }

  private renderBestiary(): void {
    const level = this.getLevelDefinition();
    const all: [EnemyType, boolean][] = [
      ...Object.values(level.enemyTypes).map((e) => [e, false] as [EnemyType, boolean]),
      ...Object.values(level.bossTypes).map((e) => [e, true] as [EnemyType, boolean]),
    ];
    const grid = document.createElement('div');
    Object.assign(grid.style, { display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${this.compact ? 210 : 220}px, 1fr))`, gap: this.compact ? '10px' : '10px' });
    for (const [type, isBoss] of all) {
      const card = document.createElement('div');
      Object.assign(card.style, {
        display: 'flex',
        gap: this.compact ? '10px' : '10px',
        padding: this.compact ? '10px' : '10px',
        border: `1px solid ${isBoss ? '#5a2030' : '#2a2438'}`,
        borderRadius: '6px',
        background: 'rgba(20,16,28,0.6)',
      });

      const art = document.createElement('div');
      Object.assign(art.style, { flex: '0 0 auto', width: this.compact ? '52px' : '64px', display: 'grid', placeItems: 'center' });
      art.appendChild(spriteToCanvas(type.sprite, this.compact ? 3 : 4));

      const info = document.createElement('div');
      info.style.fontSize = this.compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '11px';
      const name = document.createElement('div');
      const enemyName = document.createElement('b');
      enemyName.textContent = NAMES[type.key] ?? type.key;
      enemyName.style.color = isBoss ? '#ff8aa0' : '#e8e4f0';
      name.appendChild(enemyName);
      if (isBoss) {
        const boss = document.createElement('span');
        boss.textContent = ' BOSS';
        boss.style.color = '#ff6a80';
        name.appendChild(boss);
      }
      name.style.marginBottom = '4px';
      const stats = document.createElement('div');
      stats.style.color = '#9fd4ff';
      stats.textContent = `HP ${type.hp} · DMG ${type.contactDmg} · SPD ${type.speedPerSec} · XP ${type.xp}`;
      const special = document.createElement('div');
      special.style.color = '#cdc7dd';
      special.style.marginTop = '4px';
      special.textContent = ENEMY_INFO[type.key] ?? KIND_INFO[type.kind] ?? '';
      info.append(name, stats, special);
      card.append(art, info);
      grid.appendChild(card);
    }
    const note = document.createElement('div');
    note.textContent = `${level.displayName}: contact damage shown is the base value; enemies hit ~6% harder for every minute survived.`;
    Object.assign(note.style, { color: '#9a93b0', fontSize: this.compact ? `${COMPACT_MENU_CONTENT_FONT_PX}px` : '11px', marginTop: this.compact ? '12px' : '14px' });
    this.body.append(grid, note);
  }

  private renderLeaderboard(): void {
    if (this.client.usesNativeLeaderboard) {
      const title = document.createElement('div');
      title.textContent = 'GAME CENTER';
      Object.assign(title.style, { color: '#ffd9a0', letterSpacing: this.compact ? '1px' : '2px', textAlign: 'center', marginBottom: this.compact ? '12px' : '12px', fontSize: this.compact ? `${COMPACT_MENU_TITLE_FONT_PX}px` : '13px' });
      const detail = document.createElement('div');
      detail.textContent = 'View your Brewmancer rank and scores in Game Center.';
      Object.assign(detail.style, { color: '#cdc7dd', textAlign: 'center', lineHeight: '1.5', margin: this.compact ? '0 auto 16px' : '0 auto 18px', maxWidth: '360px' });
      const open = document.createElement('button');
      open.textContent = 'OPEN LEADERBOARD';
      Object.assign(open.style, {
        display: 'block',
        margin: '0 auto',
        padding: this.compact ? '9px 12px' : '10px 16px',
        border: '2px solid #4a4366',
        borderRadius: '6px',
        background: 'rgba(34, 25, 54, 0.9)',
        color: '#e8e4f0',
        font: `${this.compact ? 11 : 12}px "Silkscreen", ui-monospace, monospace`,
        cursor: 'pointer',
      });
      open.addEventListener('click', () => this.client.openNativeLeaderboard());
      this.body.append(title, detail, open);
      return;
    }

    const title = document.createElement('div');
    title.textContent = this.compact ? 'TOP SCORES' : 'GLOBAL LEADERBOARD · TOP 20';
    Object.assign(title.style, { color: '#ffd9a0', letterSpacing: this.compact ? '1px' : '2px', textAlign: 'center', marginBottom: this.compact ? '12px' : '12px', fontSize: this.compact ? `${COMPACT_MENU_TITLE_FONT_PX}px` : '13px' });
    const status = document.createElement('div');
    status.textContent = 'Loading...';
    Object.assign(status.style, { color: '#9a93b0', textAlign: 'center', padding: this.compact ? '8px 0' : '12px 0' });
    this.body.append(title, status);
    void this.client.fetchLeaderboard().then((entries) => {
      if (this.active !== 'Leaderboard' || !this.open) return; // tab switched / closed while loading
      status.replaceWith(renderBoard(entries));
    });
  }
}
